// detailRailSx.js
import { DETAIL_RAIL_WIDTH, SP } from '../../theme/tokens';

/**
 * detailRailSx — Breite und Klebebedingung der Detail-Rail in EINEM Vertrag (BW7, UI2 Teil 10, G0b.4).
 *
 * @description
 *   Eine Detailseite trägt neben der Hauptspalte eine **Rail**: eine zweite
 *   Kartenspalte, die beim Rollen im Bild bleibt. Heute steht ihre Breite
 *   **dreimal** als nackte Zahl `320` im Baum (PO-Detail, Fall-Detail,
 *   K+N-Sendung; Konzept `inhaltsbreite.md` §0.2:60, §2.3:335) — und **keine**
 *   dieser drei Stellen klebt (PW9 wäre dort rot). Dieser Baustein ist der eine
 *   Vertrag: die Breite kommt aus `DETAIL_RAIL_WIDTH`, die Klebebedingung steht
 *   daneben statt in jeder Seite neu.
 *
 *   🔴 **Der Abstand `top` ist die Formel des Konzepts, nicht eine neue Zahl.**
 *   §BW7:827 verlangt `top: kopf + SP.card` — die Höhe der **festen Kopfleiste**
 *   plus ein Kartenabstand. Die Kopfleiste des Hauses ist `64` px; sie steht
 *   zweimal als lokale Konstante (`SidebarAndHeaderBar.jsx:517`, `:1081`) und
 *   `:1917` rechnet für ihren eigenen Einzug **dieselbe** Summe
 *   (`` pt: `${headerHeight + 16}px` ``). `64` ist **kein Token** — sie hier zu
 *   nennen ist der ehrlichere Weg als eine zweite Zahl zu erfinden; ein
 *   Kopfleisten-Token ist ein eigener Bauauftrag, kein Nebensatz.
 *
 *   🔴 **Die Klebebedingung ist der zweite Teil des Vertrags** (PW9b:1014): eine
 *   Rail, die **höher** ist als der Bildschirm, darf nicht unbedingt kleben —
 *   sonst bliebe ihr Fuß unerreichbar. Deshalb trägt der Vertrag `maxHeight`
 *   (`100dvh` minus den Klebeabstand) **und** `overflowY: 'auto'`: die lange Rail
 *   rollt in sich selbst, und ihr Fuß bleibt erreichbar. 🔴 *Abgeleitet, nicht
 *   Owner-Wortlaut:* die Konzeptzeile §BW7:826 nennt Breite und `sticky`, nicht
 *   den langen Fall; PW9b ist die Prüfung, die ihn verlangt.
 *
 *   🔴 **Opt-in.** Dieser Baustein rendert nichts und ändert nichts von selbst:
 *   die drei handgeschriebenen Rail-`Stack`s ziehen in **G0b.5** um (Plan
 *   §3.3:694). Bis dahin ist er ein Vorrat mit Probe — dieselbe Bauform wie die
 *   Tokens aus G0b.1 und die Hooks aus G0b.3.
 *
 * @useWhen
 *   - Eine Detailseite (`WF1`) legt eine zweite Kartenspalte neben die Hauptspalte
 *   - Die Rail soll beim Rollen im Bild bleiben (PW9) und trotzdem ganz erreichbar sein (PW9b)
 *
 * @dontUseFor
 *   - Die Hauptspalte einer Seite — die wächst, sie klebt nicht
 *   - Die 3-px-Status-Schiene — das ist `RAIL_WIDTH` (Konzept §0.2:71, Grill B1), ein anderer Baustein
 *   - Eine Rail **ohne** feste Kopfleiste — dann ist `top` zu groß (der Aufrufer setzt sein eigenes `sx`, das gewinnt)
 *
 * @related
 *   - `CONTENT_WIDTH`/`DETAIL_RAIL_WIDTH` (`theme/tokens.js`) — die eine Quelle der Breiten
 *   - `PageContainer` — die Seite, in deren Muster die Rail steht (`wide` ab 1920, OE-W1)
 *   - `useContainerWidth` (BW4) — liefert später die Breite, aus der `narrow`/`wide` gewählt wird
 *
 * @reuseHints
 *   - Die drei Rails (PO-Detail, Fall-Detail, K+N-Sendung) sind die ersten drei Aufrufer (G0b.5)
 *   - Eine vierte Rail auf einer `wide`-Seite nimmt `DETAIL_RAIL_WIDE_SX` statt einer eigenen Zahl
 */

/** Die feste Kopfleiste des Hauses in px — kein Token, s. o. */
const KOPFLEISTE_PX = 64;

/** Der MUI-Spacing-Faktor in px — `theme.spacing(1)` = 8 px
 *  (`theme/tokens.js` §„HOW THE SPACING SYSTEM WORKS").
 *
 *  🔴 Er steht hier als EIGENE Konstante, und das ist eine begründete Abweichung
 *  von der Hausform `theme.spacing(SP.x)` (R16-W5): dieser Vertrag ist ein
 *  **nacktes Objekt** und bekommt kein Theme. `RAIL_STICKY_TOP` und das
 *  `calc()` darunter werden VOR jedem Theme gebaut; `theme.spacing(SP.card)` ist
 *  dort nicht erreichbar. Bis G0b.4 stand hier `SP.card * 8` — dieselbe Zahl als
 *  Literal in einer zweiten Zeile. IN `sx` bleibt `theme.spacing(SP.x)` die
 *  Hausform. */
const MUI_SPACING_PX = 8;

/** Ein Kartenabstand in px — `theme.spacing(SP.card)`; `SP` zählt Spacing-Einheiten
 *  zum Faktor oben. */
const KARTENABSTAND_PX = SP.card * MUI_SPACING_PX;

/** Der Klebeabstand der Rail — die Konzeptformel `kopf + SP.card` (§BW7:827). */
export const RAIL_STICKY_TOP = KOPFLEISTE_PX + KARTENABSTAND_PX;

/** Die Rail als Flex-Geschwister der Hauptspalte — Breite, kein Schrumpfen, oben ausgerichtet.
 *
 *  🔴 `alignSelf: 'flex-start'` ist **nicht** Kosmetik: in einem Flex-Elternteil
 *  streckt sich ein Kind sonst auf die volle Zeilenhöhe, und `position: sticky`
 *  hat dann nichts zu greifen (die Box ist bereits so hoch wie der Inhalt). */
export const DETAIL_RAIL_SX = {
  width: DETAIL_RAIL_WIDTH.narrow,
  flexShrink: 0,
  alignSelf: 'flex-start',
};

/** Die Hoehengrenze der klebenden Flaeche — EINE Quelle fuer `DETAIL_RAIL_STICKY_SX`
 *  und jeden Aufrufer, der die Klebeform ohne die feste Rail-Breite braucht
 *  (`Vorschlagsteil` — dort waechst das Detail, es hat keine 320er Breite).
 *
 *  🔴 **Ohne sie ist der Fuss nicht erreichbar** (R15-N2 / R16-N2, 2026-11-03):
 *  ein `position: sticky` ohne `maxHeight`/`overflowY` klebt oben, und ein
 *  Detail, das hoeher ist als der Viewport, endet unter dem Bildrand — die
 *  Bestaetigung ist dann nicht zu treffen. */
export const RAIL_STICKY_MAX_HEIGHT = `calc(100dvh - ${RAIL_STICKY_TOP}px - ${KARTENABSTAND_PX}px)`;

/** Die klebende Rail — `DETAIL_RAIL_SX` plus die Klebebedingung (PW9/PW9b). */
export const DETAIL_RAIL_STICKY_SX = {
  ...DETAIL_RAIL_SX,
  position: 'sticky',
  top: RAIL_STICKY_TOP,
  maxHeight: RAIL_STICKY_MAX_HEIGHT,
  overflowY: 'auto',
};

/** Die Rail einer `wide`-Seite (640, `DETAIL_RAIL_WIDTH.wide`) — sonst wie die klebende. */
export const DETAIL_RAIL_WIDE_SX = {
  ...DETAIL_RAIL_STICKY_SX,
  width: DETAIL_RAIL_WIDTH.wide,
};
